import { useEffect, useState, useRef } from "react";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import { env } from "@/core/config/env";
  
const TWO_LETTER_CODE = /^[A-Z]{2}$/;
const toTwoLetterCode = (code: string | undefined | null): string | null =>
  code && TWO_LETTER_CODE.test(code) ? code : null;

export type GeocodedAddressData = {
  description: string;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  county: string | null;
  zipCode: string | null;
  latitude: number | null;
  longitude: number | null;
  // Feature 099 handoff (handoff-work-location-fields.md): structured codes for
  // Checkr work_locations, captured at this same selection event. `state` above
  // stays untouched (long_name, e.g. "Texas") for whatever already consumes it —
  // these are additive, short_name codes (e.g. "TX"/"US").
  stateUspsCode: string | null;
  countryIsoCode: string | null;
}

type AddressAutocompleteProps = {
  selectedAddress: string;
  setSelectedAddress: (address: string) => void;
  onAddressSelectAndGeocode: (data: GeocodedAddressData) => void;
  resetTrigger: number;
}

const libraries: ('places')[] = ['places'];

const AddressAutocomplete = ({
  selectedAddress,
  setSelectedAddress,
  onAddressSelectAndGeocode,
  resetTrigger
}: AddressAutocompleteProps) => {

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isUserTypingRef = useRef(false);
  const isInputFocusedRef = useRef(false);
  const [inputValue, setInputValue] = useState(selectedAddress);
  const googleMapsApiKey = env.GOOGLE_MAPS_API_KEY || '';
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries,
  });

  useEffect(() => {
    setInputValue("");
    isUserTypingRef.current = false;
    isInputFocusedRef.current = false;
  }, [resetTrigger]);

  // Only sync from parent when user is not actively typing and input is not focused
  // This prevents focus loss while the user is typing
  useEffect(() => {
    if (!isUserTypingRef.current && !isInputFocusedRef.current && selectedAddress !== inputValue) {
      setInputValue(selectedAddress);
    }
  }, [selectedAddress, inputValue]);

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      
      if (!place.geometry || !place.geometry.location) {
        // User entered the name of a Place that was not suggested
        setInputValue("");
        onAddressSelectAndGeocode({
          description: place.name || "",
          streetAddress: null,
          city: null,
          state: null,
          county: null,
          zipCode: null,
          latitude: null,
          longitude: null,
          stateUspsCode: null,
          countryIsoCode: null,
        });
        return;
      }

      const description = place.formatted_address || "";
      const latitude = place.geometry.location.lat();
      const longitude = place.geometry.location.lng();

      let streetNumber = "";
      let route = "";
      let finalCity = "";
      let finalState = "";
      let finalCounty = "";
      let finalZipCode = "";
      let finalStateUspsCode = "";
      let finalCountryIsoCode = "";

      let tempLocality: string | null = null;
      let tempPostalTown: string | null = null;
      let tempSublocalityLevel1: string | null = null;

      if (place.address_components) {
        place.address_components.forEach(component => {
          const types = component.types;
          const name = component.long_name;

          if (types.includes("street_number")) streetNumber = name;
          if (types.includes("route")) route = name;
          if (types.includes("administrative_area_level_1")) {
            finalState = name;
            finalStateUspsCode = component.short_name;
          }
          if (types.includes("administrative_area_level_2")) finalCounty = name;
          if (types.includes("postal_code")) finalZipCode = name;
          if (types.includes("country")) finalCountryIsoCode = component.short_name;

          if (types.includes("locality")) tempLocality = name;
          if (types.includes("postal_town")) tempPostalTown = name;
          if (types.includes("sublocality_level_1")) tempSublocalityLevel1 = name;
        });
      }

      if (tempLocality) {
        finalCity = tempLocality;
      } else if (tempPostalTown) {
        finalCity = tempPostalTown;
      } else if (tempSublocalityLevel1) {
        finalCity = tempSublocalityLevel1;
      }

      const streetAddress = `${streetNumber} ${route}`.trim();
      
      onAddressSelectAndGeocode({
        description,
        streetAddress: streetAddress || null,
        city: finalCity || null,
        state: finalState || null,
        county: finalCounty || null,
        zipCode: finalZipCode || null,
        latitude: latitude ?? null,
        longitude: longitude ?? null,
        // Google's short_name isn't always a 2-letter code (e.g. "England") —
        // emit null instead, matching backend geocoding, so max(2) never blocks a save.
        stateUspsCode: toTwoLetterCode(finalStateUspsCode),
        countryIsoCode: toTwoLetterCode(finalCountryIsoCode),
      });

      setInputValue(description);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    isUserTypingRef.current = true;
    setInputValue(value);
    setSelectedAddress(value);
    // If user clears the input, also clear the geocoded data
    if (!value || value.trim() === '') {
      onAddressSelectAndGeocode({
        description: '',
        streetAddress: null,
        city: null,
        state: null,
        county: null,
        zipCode: null,
        latitude: null,
        longitude: null,
        stateUspsCode: null,
        countryIsoCode: null,
      });
    }
    // Reset typing flag after a short delay to allow parent updates
    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 100);
  };

  const handleInputFocus = () => {
    isInputFocusedRef.current = true;
    // Preserve focus by ensuring input ref is set
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputBlur = () => {
    isInputFocusedRef.current = false;
    // Reset typing flag when user leaves the field
    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 200);
  };

  const hasGoogleMaps =
    isLoaded && typeof window !== 'undefined' && Boolean(window.google?.maps?.places);

  // If no API key or error, show a fallback input
  if (!googleMapsApiKey || loadError || !hasGoogleMaps) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        className="font-termina input-ghost rounded-md w-full h-full bg-gray-200 border-gray-600 placeholder:text-gray-600"
        placeholder="Enter zip code or address"
      />
    );
  }

  return (
    <div className="w-full flex flex-col relative">
      <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
        <input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="font-termina input-ghost rounded-md w-full h-full bg-gray-200 border-gray-600 placeholder:text-gray-600"
          placeholder="Search by address"
        />
      </Autocomplete>
    </div>
  );
};

export default AddressAutocomplete;

